import { defineStore } from 'pinia'
import { ref } from 'vue'
import { tasksApi } from '@/plugins/api'
import {
  loadFromCache,
  saveToCache,
  mergeCache,
  updateCacheEntry,
  removeCacheEntry,
} from '@/utils/task-cache'
import { useDepartmentStore } from './department'

export interface Comment {
  id: string
  content: string
  source: string
  createdAt: string
  author?: { id: string; fullName: string } | null
  sourceRef?: {
    // GitLab commit fields
    commitId?: string
    commitShortId?: string
    commitSha?: string
    commitUrl?: string
    authorName?: string
    projectId?: string
    projectName?: string
    // Time tracking fields
    type?: string // 'time_tracking' for time entry comments
    durationMinutes?: number
    timeEntryId?: string
    migratedAt?: string
    // Поля комментариев из писем
    emailId?: string
    fromAddress?: string
    fromName?: string
    chainBlock?: boolean
  }
}

export interface Attachment {
  id: string
  filename: string
  originalFilename: string
  mimeType: string | null
  sizeBytes: number | null
  storagePath: string
  createdAt: string
  uploadedBy?: { id: string; fullName: string } | null
}

export interface TaskProject {
  id: string
  taskId: string
  projectId: string
  isPrimary: boolean
  addedAt: string
  project?: { id: string; title: string; slug: string }
}

export interface Task {
  id: string
  humanId?: string // e.g., ":t100003"
  title: string
  description: string | null
  status: string
  priority: string
  prioritySort?: number // 1-999, default 100, lower = higher within same priority
  progress?: number // AI-estimated progress 0-100
  startDate: string | null
  plannedDate: string | null
  plannedDateIsDefault?: boolean // дата подставлена автоматически — не срок
  isStanding?: boolean // «постоянная» задача: учёт времени без срока и прогноза
  hardDeadline: string | null
  responsibleDepartmentId: string | null
  tags: string[]
  excludedAiTags?: string[]
  /** Теги, поставленные автоклассификатором группы (не человеком) */
  autoTags?: string[]
  isArchived?: boolean
  archivedAt?: string | null
  createdAt: string
  updatedAt: string
  taskOwners?: Array<{ person: { id: string; fullName: string; keycloakId?: string }; role?: string }>
  taskProjects?: TaskProject[]
  responsibleDepartment?: { id: string; name: string } | null
  comments?: Comment[]
  attachments?: Attachment[]
}

export interface TasksFilter {
  search?: string
  status?: string
  priority?: string
  ownerId?: string
  departmentId?: string
  projectId?: string
  hasNoProject?: boolean
  hasNoOwners?: boolean
  hasNoTags?: boolean
  excludeStatus?: string
  excludeProjectIds?: string
  tags?: string
  // Date filters
  overdue?: boolean
  startDateFrom?: string
  startDateTo?: string
  plannedDateFrom?: string
  plannedDateTo?: string
  deadlineFrom?: string
  deadlineTo?: string
  completedDateFrom?: string
  completedDateTo?: string
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  // Include deleted tasks (normally excluded)
  includeDeleted?: boolean
  // Include cancelled tasks (normally excluded)
  includeCancelled?: boolean
  // Include archived tasks (normally excluded from active queries)
  includeArchived?: boolean
  // Show ONLY archived tasks
  archivedOnly?: boolean
  // Delta sync timestamp (ISO 8601)
  updatedSince?: string
}

export type LoadingStage = 'idle' | 'cache' | 'recent' | 'week' | 'full' | 'delta' | 'done'

/**
 * Check if filters contain content-affecting parameters that make caching impractical.
 * The cache stores the full unfiltered dataset; filtered requests go directly to API.
 */
function hasContentFilters(filters: TasksFilter): boolean {
  return !!(
    filters.search ||
    filters.status ||
    filters.priority ||
    filters.ownerId ||
    filters.projectId ||
    filters.hasNoProject ||
    filters.hasNoOwners ||
    filters.hasNoTags ||
    filters.excludeStatus ||
    filters.excludeProjectIds ||
    filters.tags ||
    filters.overdue ||
    filters.startDateFrom ||
    filters.startDateTo ||
    filters.plannedDateFrom ||
    filters.plannedDateTo ||
    filters.deadlineFrom ||
    filters.deadlineTo ||
    filters.completedDateFrom ||
    filters.completedDateTo ||
    filters.includeDeleted ||
    filters.includeCancelled ||
    filters.includeArchived ||
    filters.archivedOnly
  )
}

function daysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString()
}

/** Convert typed filters to plain Record for API call */
function toParams(filters: TasksFilter): Record<string, unknown> {
  return { ...filters } as Record<string, unknown>
}

export const useTasksStore = defineStore('tasks', () => {
  const tasks = ref<Task[]>([])
  const currentTask = ref<Task | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const total = ref(0)
  const page = ref(1)
  const limit = ref(20)

  // SWR state
  const loadingStage = ref<LoadingStage>('idle')
  const isBackgroundRefreshing = ref(false)
  const isFullyLoaded = ref(false) // guard: prevent duplicate full loads from multiple views

  // Полная загрузка, которая уже идёт. Несколько экранов (дашборд, учёт
  // времени, отчёты) вызывают fetchTasks({ limit: -1 }) почти одновременно:
  // флаг isFullyLoaded ставится только по завершении, и каждый вызов слал
  // свою пару запросов. Повторный вызов с тем же ключом ждёт текущую загрузку
  let fullLoadInFlight: { key: string; promise: Promise<void> } | null = null

  function getDepartmentId(): string | null {
    try {
      const deptStore = useDepartmentStore()
      return deptStore.currentDepartmentId ?? null
    } catch {
      return null
    }
  }

  /**
   * Fetch tasks with Stale-While-Revalidate caching for full (unfiltered) loads.
   *
   * - Full load (limit=-1, no filters): SWR from localStorage cache → background delta sync
   * - Filtered load: direct API call (no cache)
   * - Cold start (no cache): progressive loading in 3 stages
   */
  async function fetchTasks(filters: TasksFilter = {}) {
    error.value = null
    const isFullLoad = filters.limit === -1 && !hasContentFilters(filters)
    const departmentId = getDepartmentId()

    if (isFullLoad) {
      // Guard: skip if already loaded (cache shown + delta synced)
      if (isFullyLoaded.value && tasks.value.length > 0) return

      // Ключ — подразделение и параметры: смена подразделения во время
      // загрузки запускает новую, а не подхватывает чужую
      const key = JSON.stringify([departmentId, toParams(filters)])
      if (fullLoadInFlight?.key === key) return fullLoadInFlight.promise

      const promise = runFullLoad(filters, departmentId)
      const entry = { key, promise }
      fullLoadInFlight = entry
      try {
        await promise
      } finally {
        if (fullLoadInFlight === entry) fullLoadInFlight = null
      }
      return
    }

    // Filtered or paginated request — standard fetch (no cache)
    // Mark store as not fully loaded so next unfiltered fetch won't skip via SWR guard
    isFullyLoaded.value = false
    loading.value = true
    try {
      const response = await tasksApi.getAll(toParams({
        ...filters,
        page: filters.page || page.value,
        limit: filters.limit || limit.value,
      }))
      tasks.value = response.data.data
      total.value = response.data.total
      page.value = response.data.page
      limit.value = response.data.limit
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch tasks'
      console.error('Failed to fetch tasks:', e)
    } finally {
      loading.value = false
      loadingStage.value = 'done'
    }
  }

  /**
   * Полная загрузка: SWR из localStorage + дельта, либо холодный старт в три
   * этапа. Ошибки перехватываются внутри — общий promise не отклоняется
   * (не все вызывающие делают await)
   */
  async function runFullLoad(filters: TasksFilter, departmentId: string | null) {
    const cached = loadFromCache(departmentId)

    if (cached) {
      // SWR: show cached data immediately, then delta sync in background
      loadingStage.value = 'cache'
      tasks.value = cached.tasks
      total.value = cached.tasks.length
      loading.value = false

      // Background delta sync
      isBackgroundRefreshing.value = true
      loadingStage.value = 'delta'
      try {
        const [freshResp, deletedResp] = await Promise.all([
          tasksApi.getAll(toParams({ ...filters, updatedSince: cached.serverTimestamp })),
          tasksApi.getDeletedSince(cached.serverTimestamp),
        ])
        const freshData = freshResp.data.data ?? freshResp.data
        const serverTimestamp = freshResp.data.serverTimestamp
        const deletedIds = deletedResp.data.deletedIds ?? []

        const merged = mergeCache(cached.tasks, freshData, deletedIds)
        tasks.value = merged
        total.value = merged.length

        if (serverTimestamp) {
          saveToCache(merged, serverTimestamp, departmentId)
        }
      } catch (e) {
        console.warn('[TasksStore] Background delta sync failed:', e)
      } finally {
        isBackgroundRefreshing.value = false
        isFullyLoaded.value = true
        loadingStage.value = 'done'
      }
      return
    }

    // Cold start: progressive loading (3 stages)
    loading.value = true
    try {
      // Stage 1: last 3 days
      loadingStage.value = 'recent'
      const recentResp = await tasksApi.getAll(toParams({ ...filters, updatedSince: daysAgo(3) }))
      const recentData = recentResp.data.data ?? recentResp.data
      tasks.value = recentData
      total.value = recentData.length
      loading.value = false // Unblock UI after first stage

      // Stage 2: last 7 days
      loadingStage.value = 'week'
      isBackgroundRefreshing.value = true
      const weekResp = await tasksApi.getAll(toParams({ ...filters, updatedSince: daysAgo(7) }))
      const weekData = weekResp.data.data ?? weekResp.data
      tasks.value = mergeCache(tasks.value, weekData, [])
      total.value = tasks.value.length

      // Stage 3: all tasks
      loadingStage.value = 'full'
      const fullResp = await tasksApi.getAll(toParams(filters))
      const fullData = fullResp.data.data ?? fullResp.data
      const serverTimestamp = fullResp.data.serverTimestamp
      tasks.value = fullData
      total.value = fullData.length

      if (serverTimestamp) {
        saveToCache(fullData, serverTimestamp, departmentId)
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch tasks'
      console.error('[TasksStore] Progressive loading failed:', e)
    } finally {
      loading.value = false
      isBackgroundRefreshing.value = false
      isFullyLoaded.value = true
      loadingStage.value = 'done'
    }
  }

  async function fetchTask(id: string) {
    loading.value = true
    error.value = null
    try {
      const response = await tasksApi.getOne(id)
      currentTask.value = response.data
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to fetch task'
      console.error('Failed to fetch task:', e)
    } finally {
      loading.value = false
    }
  }

  async function createTask(data: Partial<Task>) {
    loading.value = true
    error.value = null
    try {
      const response = await tasksApi.create(data)
      tasks.value.unshift(response.data)
      // Write-through to cache
      updateCacheEntry(response.data, getDepartmentId())
      return response.data
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to create task'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function updateTask(id: string, data: Partial<Task> & { statusChangeComment?: string }) {
    loading.value = true
    error.value = null
    try {
      // Remove id from data - it's already in URL
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id: _id, ...updateData } = data
      const response = await tasksApi.update(id, updateData)
      const index = tasks.value.findIndex((t) => t.id === id)
      if (index !== -1) {
        tasks.value[index] = response.data
        // Write-through to cache
        updateCacheEntry(response.data, getDepartmentId())
      }
      if (currentTask.value?.id === id) {
        currentTask.value = response.data
      }
      return response.data
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to update task'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function deleteTask(id: string) {
    loading.value = true
    error.value = null
    try {
      await tasksApi.delete(id)
      tasks.value = tasks.value.filter((t) => t.id !== id)
      if (currentTask.value?.id === id) {
        currentTask.value = null
      }
      // Write-through: remove from cache
      removeCacheEntry(id, getDepartmentId())
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to delete task'
      throw e
    } finally {
      loading.value = false
    }
  }

  // WebSocket real-time update methods

  /** Update task in list from WebSocket event (optimistic update) */
  function updateTaskFromWebSocket(taskId: string, changes: Partial<Task>) {
    const index = tasks.value.findIndex((t) => t.id === taskId)
    if (index !== -1) {
      Object.assign(tasks.value[index], changes)
      // Write-through to cache
      updateCacheEntry(tasks.value[index], getDepartmentId())
    }
    // Also update currentTask if it matches
    if (currentTask.value?.id === taskId) {
      Object.assign(currentTask.value, changes)
    }
  }

  /** Remove task from list (called on delete events) */
  function removeTaskFromList(taskId: string) {
    tasks.value = tasks.value.filter((t) => t.id !== taskId)
    if (currentTask.value?.id === taskId) {
      currentTask.value = null
    }
    // Write-through to cache
    removeCacheEntry(taskId, getDepartmentId())
  }

  /** Add new task to list (called on create events) */
  function addTaskToList(task: Task) {
    // Avoid duplicates
    if (!tasks.value.some((t) => t.id === task.id)) {
      tasks.value.unshift(task)
      total.value++
      // Write-through to cache
      updateCacheEntry(task, getDepartmentId())
    }
  }

  /** Replace full task data in list (called when we need to refresh from API) */
  function replaceTaskInList(task: Task) {
    const index = tasks.value.findIndex((t) => t.id === task.id)
    if (index !== -1) {
      tasks.value[index] = task
    }
    if (currentTask.value?.id === task.id) {
      currentTask.value = task
    }
    // Write-through to cache
    updateCacheEntry(task, getDepartmentId())
  }

  return {
    tasks,
    currentTask,
    loading,
    error,
    total,
    page,
    limit,
    // SWR state
    loadingStage,
    isBackgroundRefreshing,
    isFullyLoaded,
    // Actions
    fetchTasks,
    fetchTask,
    createTask,
    updateTask,
    deleteTask,
    // WebSocket methods
    updateTaskFromWebSocket,
    removeTaskFromList,
    addTaskToList,
    replaceTaskInList,
  }
})
